import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { ChevronDown, User, GraduationCap, Users, Shield, Check } from 'lucide-react';

const roleConfig = {
  admin: { label: 'Admin', icon: Shield, color: 'text-red-600 bg-red-50' },
  tutor: { label: 'Tutor', icon: GraduationCap, color: 'text-cyan-600 bg-cyan-50' },
  parent: { label: 'Parent', icon: Users, color: 'text-green-600 bg-green-50' },
  student: { label: 'Student', icon: User, color: 'text-blue-600 bg-blue-50' }
};

const RoleSwitcher = ({ onRoleChange }) => {
  const { user, activeRole, switchRole } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  // Don't show if user has only one role
  if (!user?.roles || user.roles.length <= 1) {
    return null;
  }

  const currentRoleConfig = roleConfig[activeRole] || roleConfig.student;
  const CurrentIcon = currentRoleConfig.icon;

  const handleRoleSwitch = (newRole) => {
    switchRole(newRole);
    setIsOpen(false);
    // Notify parent component to update the view
    if (onRoleChange) {
      onRoleChange(newRole);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${currentRoleConfig.color}`}
        data-testid="role-switcher-btn"
      >
        <CurrentIcon className="w-4 h-4" />
        <span className="text-sm font-medium">{currentRoleConfig.label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">SWITCH VIEW</p>
            </div>
            <div className="py-1">
              {user.roles.map((role) => {
                const config = roleConfig[role] || roleConfig.student;
                const Icon = config.icon;
                const isActive = role === activeRole;
                
                return (
                  <button
                    key={role}
                    onClick={() => handleRoleSwitch(role)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      isActive ? 'bg-gray-50 dark:bg-gray-700' : ''
                    }`}
                    data-testid={`switch-to-${role}-btn`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${config.color.split(' ')[0]}`} />
                      <span className="text-gray-700 dark:text-gray-300">{config.label}</span>
                    </div>
                    {isActive && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RoleSwitcher;
